import {
  ISquizSearchResponse,
  ISquizSuggestResponse,
  getSquizSearchDefaultOptions,
  getSquizSuggestDefaultOptions,
  squizBaseUrl,
  squizSearch,
  squizSearchWithParams,
  squizSuggest,
} from "./squiz";
import { isNative } from "@origin-digital/platform-helpers";

export interface ISearchResult {
  rank: number;
  title: string;
  url: string;
  summary: string;
  allSummaryText: null;
  imageUrl?: string;
  description?: string;
  displayUrl: string;
  clickTrackingUrl: string;
}

export interface IRelatedSearch {
  href: string;
  count: number;
  label: string;
  query: string;
}

export interface ISearchResultsSummary {
  fullyMatching: number;
  partiallyMatching: number;
  totalMatching: number;
  currStart: number;
  currEnd: number;
}

export interface ISearchFacetValue {
  // data: string; // All Results,
  label: string; // All Results,
  count: number; // 733,
  selected: boolean; //true,
  toggleUrl: string; // ?query=board&profile=stencil&collection=489b6fea-b9bf-4672-b021-08279da0525d%7Esp-origin-energy
}

export interface ISearchFacet {
  name: string; // Like 'Tabs'
  unselectAllUrl: string;
  values: ISearchFacetValue[];
}

export interface IPaginationBlock {
  first: IPagination;
  previous: IPagination | null;
  next: IPagination;
  pages: IPagination[];
}

export interface ISearchResponse {
  pagination?: IPaginationBlock;
  resultsSummary: ISearchResultsSummary;
  results: ISearchResult[];
  substituteQuery?: string;
  spellingSuggestion?: {
    url: string;
    text: string;
  } | null;
  facets: ISearchFacet[];
  question: {
    query: string; // what was searched for
    originalQuery: string; // the original query
  };
  relatedSearches: IRelatedSearch[];
}

export interface IPagination {
  label: string;
  url: string;
  selected: boolean;
}

export const doSearch = async (
  queryStr: string | undefined,
  env: EnvironmentNames
) => {
  const options = {
    ...getSquizSearchDefaultOptions(env, isNative()),
    ...{ query: queryStr || "!null" },
  };
  const results = await squizSearch(options);
  return transformSquizResponse(results as ISquizSearchResponse);
};

export const doSuggest = async (
  str: string,
  env: EnvironmentNames
): Promise<ISquizSuggestResponse> => {
  const options = {
    ...getSquizSuggestDefaultOptions(env, isNative()),
    ...{ partial_query: str },
  };
  const results = await squizSuggest(options);
  return results || [];
};

export const doSearchWithParams = async (
  paramStr: string,
  env: EnvironmentNames
): Promise<ISearchResponse> => {
  const results = await squizSearchWithParams(
    getSquizSearchDefaultOptions(env, isNative()),
    paramStr
  );
  return transformSquizResponse(results as ISquizSearchResponse);
};

export function transformSquizResponse(
  squizResponse: ISquizSearchResponse
): ISearchResponse {
  const pagination = squizResponse?.response?.customData?.stencils
    ?.pagination as IPaginationBlock;

  const substituteQuery =
    squizResponse?.response?.resultPacket?.qsups?.[0]?.query;

  const spellingSuggestion = squizResponse?.response.resultPacket?.spell;

  const squizResultsSummary =
    squizResponse.response.resultPacket.resultsSummary;

  const resultsSummary = {
    totalMatching: squizResultsSummary.totalMatching,
    fullyMatching: squizResultsSummary.fullyMatching,
    partiallyMatching: squizResultsSummary.partiallyMatching,
    currStart: squizResultsSummary.currStart,
    currEnd: squizResultsSummary.currEnd,
  };

  const squizResults = squizResponse?.response?.resultPacket?.results || [];
  const results = squizResults.map((squizResult) => {
    const metaData = squizResult.listMetadata;

    const imageUrl =
      (squizResult.listMetadata &&
        squizResult.listMetadata.image &&
        squizResult.listMetadata.image.length &&
        squizResult.listMetadata.image[0]) ||
      undefined;

    const summary =
      metaData && metaData.c && metaData.c.length
        ? metaData.c[0]
        : squizResult.summary;

    const result: ISearchResult = {
      rank: squizResult.rank,
      url: squizResult.liveUrl,
      displayUrl: squizResult.displayUrl,
      allSummaryText: squizResult.allSummaryText,
      // all other squiz URLs are for query params but this one is clickable,
      // so we'll make it absolute
      clickTrackingUrl: squizBaseUrl() + squizResult.clickTrackingUrl,
      title: squizResult.title,
      summary,
      imageUrl,
    };

    return result;
  });

  const squizRelatedSearches =
    squizResponse?.response?.resultPacket?.contextualNavigation?.categories ||
    [];

  const relatedSearches = squizRelatedSearches.reduce((accum, category) => {
    accum.push(
      ...category.clusters.map((c) => ({
        href: c.href,
        count: c.count,
        label: c.label,
        query: c.query,
      }))
    );
    return accum;
  }, [] as IRelatedSearch[]);

  const squizFacets = squizResponse.response.facets;
  const facets: ISearchFacet[] = squizFacets
    .map((squizFacet) => {
      return {
        name: squizFacet.name,
        unselectAllUrl: squizFacet.unselectAllUrl,
        values: squizFacet.allValues
          .filter((v) => !!v.count) // only include values with a count
          .map((v) => ({
            label: v.label,
            count: v.count,
            selected: v.selected,
            toggleUrl: v.toggleUrl,
          })),
      };
    })
    .filter((f) => !!f.values.length); // remove any with empty values array

  const question = {
    query: squizResponse.question.query,
    originalQuery: squizResponse.question.originalQuery,
  };

  const retVal: ISearchResponse = {
    question,
    resultsSummary,
    results,
    relatedSearches,
    substituteQuery,
    spellingSuggestion,
    pagination,
    facets,
  };

  return retVal;
}
